import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { tenantApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export default function AdminBilling() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const tenantId = user?.tenantId;

  const load = () => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      tenantApi.get(`/tenants/${tenantId}/subscription`),
      tenantApi.get(`/tenants/${tenantId}/invoices`),
    ])
      .then(([sub, inv]) => {
        setSubscription(sub.data);
        setInvoices(inv.data);
      })
      .catch((err) => { const m = errorMessage(err); if (m) setError(m); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [tenantId]);

  const cancel = async () => {
    const reason = window.prompt("Reason for cancellation?");
    if (!reason) return;
    setCanceling(true);
    try {
      await tenantApi.post(`/tenants/${tenantId}/subscription/cancel`, { reason });
      load();
    } catch (err) {
      const m = errorMessage(err); if (m) setError(m);
    } finally {
      setCanceling(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Billing & Subscription</h1>
      <p className="mt-1 text-muted-foreground">Plan, invoices and payment history.</p>

      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}
      {error && <p className="mt-8 text-sm text-destructive">{error}</p>}

      {subscription && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="capitalize">{subscription.plan_code} plan</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <Badge variant={subscription.status === "active" ? "success" : "secondary"}>{subscription.status}</Badge>
            {subscription.status !== "canceled" && (
              <Button variant="destructive" size="sm" onClick={cancel} disabled={canceling} data-testid="cancel-subscription-btn">
                Cancel subscription
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Invoice history</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Period</th>
                  <th className="py-2">Amount</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b last:border-0">
                    <td className="py-2">{new Date(inv.period_start).toLocaleDateString()}</td>
                    <td className="py-2">${(inv.amount_cents / 100).toFixed(2)}</td>
                    <td className="py-2 capitalize">{inv.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
